import i18n from '@/utils/i18n';
import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  Icons,
  List,
  ScrollView,
  Typography,
} from '@usepepper/sprout-kit-ui';
import { ConvertQuoteGroupView } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/hooks/use-convert-to-price-quote-selection';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { useTheme } from '@/contexts/theme-context';

const DIALOG_WIDTH = 480;
const DIALOG_HEIGHT = 720;

/* Rows are memoized so toggling a checkbox re-renders only the affected row
   and its group header instead of every row in the modal — on large guides
   the full-list re-render costs hundreds of ms of INP. */

interface GroupRowProps {
  checkedState: ConvertQuoteGroupView['checkedState'];
  isExpanded: boolean;
  name: string;
  onToggle: (partitionUUID: string) => void;
  onToggleExpanded: (partitionUUID: string) => void;
  partitionUUID: string;
  selectedCount: number;
  total: number;
}

const GroupRow = React.memo<GroupRowProps>(
  ({
    checkedState,
    isExpanded,
    name,
    onToggle,
    onToggleExpanded,
    partitionUUID,
    selectedCount,
    total,
  }) => {
    const theme = useTheme();

    return (
      <List.Item paddingHorizontal={0}>
        <List.Item.Leading>
          <Checkbox
            accessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.convert_to_price_quote.select_group',
              { group: name },
            )}
            checked={checkedState}
            onCheckedChange={() => onToggle(partitionUUID)}
            size="$sm"
          />
        </List.Item.Leading>
        <List.Item.Content>
          <List.Item.Title fontWeight="$medium">{name}</List.Item.Title>
        </List.Item.Content>
        <List.Item.Trailing
          accessibilityRole="button"
          aria-expanded={isExpanded}
          cursor="pointer"
          flexDirection="row"
          gap="$8"
          onPress={() => onToggleExpanded(partitionUUID)}
        >
          <Typography color={theme.sk.color.text.secondary} size="$xs">
            {`${selectedCount}/${total}`}
          </Typography>
          {isExpanded ? (
            <Icons.ChevronUp color={theme.sk.color.icon.tertiary} size="$16" />
          ) : (
            <Icons.ChevronDown
              color={theme.sk.color.icon.tertiary}
              size="$16"
            />
          )}
        </List.Item.Trailing>
      </List.Item>
    );
  },
);

interface PackRowProps {
  onToggle: (partitionUUID: string, variantPackUUID: string) => void;
  pack: OrderGuideItem;
  partitionUUID: string;
  selected: boolean;
}

const PackRow = React.memo<PackRowProps>(
  ({ onToggle, pack, partitionUUID, selected }) => {
    const theme = useTheme();

    return (
      <List.Item
        accessibilityLabel={i18n.t(
          'sales_portal.order_guides_list.selection.select_item',
          { name: pack.displayName },
        )}
        accessibilityRole="checkbox"
        aria-checked={selected}
        hoverStyle={{
          backgroundColor: theme.sk.color.bg.primary.hover,
        }}
        onPress={() => onToggle(partitionUUID, pack.variantPackUUID)}
        paddingLeft="$16"
        paddingRight={0}
        pressable
        size="$sm"
      >
        <List.Item.Leading>
          <Checkbox
            checked={selected}
            pointerEvents="none"
            size="$sm"
            tabIndex={-1}
          />
        </List.Item.Leading>
        <List.Item.Content>
          <List.Item.Title lineHeight="$sm">{pack.displayName}</List.Item.Title>
          {pack.packDisplayString ? (
            <List.Item.Subtitle color="$textSecondary" lineHeight="$xs">
              {pack.packDisplayString}
            </List.Item.Subtitle>
          ) : null}
        </List.Item.Content>
      </List.Item>
    );
  },
);

interface ConvertToPriceQuoteModalProps {
  accountName: string;
  groupViews: ConvertQuoteGroupView[];
  isConverting: boolean;
  isOpen: boolean;
  isPackSelected: (partitionUUID: string, variantPackUUID: string) => boolean;
  onClose: () => void;
  onConvert: () => void;
  onToggleGroup: (partitionUUID: string) => void;
  onToggleGroupExpanded: (partitionUUID: string) => void;
  onTogglePack: (partitionUUID: string, variantPackUUID: string) => void;
  totalSelected: number;
}

export const ConvertToPriceQuoteModal: React.FC<
  ConvertToPriceQuoteModalProps
> = ({
  accountName,
  groupViews,
  isConverting,
  isOpen,
  isPackSelected,
  onClose,
  onConvert,
  onToggleGroup,
  onToggleGroupExpanded,
  onTogglePack,
  totalSelected,
}) => {
  const theme = useTheme();

  const beforeAccount = i18n.t(
    'sales_portal.order_guides_list.convert_to_price_quote.subtitle_prefix',
  );
  const afterAccount = i18n.t(
    'sales_portal.order_guides_list.convert_to_price_quote.subtitle_suffix',
  );

  const convertLabel = i18n.t(
    totalSelected === 1
      ? 'sales_portal.order_guides_list.convert_to_price_quote.convert'
      : 'sales_portal.order_guides_list.convert_to_price_quote.convert_plural',
    { count: totalSelected },
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <Dialog.Content
        height={DIALOG_HEIGHT}
        paddingHorizontal={0}
        width={DIALOG_WIDTH}
      >
        <Dialog.Header paddingHorizontal="$24">
          <Dialog.Title>
            {i18n.t(
              'sales_portal.order_guides_list.convert_to_price_quote.title',
            )}
          </Dialog.Title>
        </Dialog.Header>

        <Typography
          color={theme.sk.color.text.secondary}
          marginBottom="$16"
          paddingHorizontal="$24"
          size="$sm"
        >
          {beforeAccount}
          <Typography bold color={theme.sk.color.text.primary} size="$sm">
            {accountName}
          </Typography>
          {afterAccount}
        </Typography>

        <ScrollView flex={1} paddingHorizontal="$24">
          {groupViews.map((group) => (
            <React.Fragment key={group.partitionUUID}>
              <GroupRow
                checkedState={group.checkedState}
                isExpanded={group.isExpanded}
                name={group.name}
                onToggle={onToggleGroup}
                onToggleExpanded={onToggleGroupExpanded}
                partitionUUID={group.partitionUUID}
                selectedCount={group.selectedCount}
                total={group.total}
              />
              {group.isExpanded
                ? group.packs.map((pack) => (
                    <PackRow
                      key={pack.variantPackUUID}
                      onToggle={onTogglePack}
                      pack={pack}
                      partitionUUID={group.partitionUUID}
                      selected={isPackSelected(
                        group.partitionUUID,
                        pack.variantPackUUID,
                      )}
                    />
                  ))
                : null}
            </React.Fragment>
          ))}
        </ScrollView>

        <Dialog.Footer fade paddingHorizontal="$24" paddingTop={0}>
          <Button onPress={onClose} size="$md" variant="secondary">
            {i18n.t(
              'sales_portal.order_guides_list.convert_to_price_quote.cancel',
            )}
          </Button>
          <Button
            disabled={
              groupViews.length === 0 || totalSelected === 0 || isConverting
            }
            loading={isConverting}
            onPress={onConvert}
            size="$md"
          >
            {convertLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
